import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  getNotificationSettings,
  saveNotificationSettings,
  NOTIFY_DEFAULT_EMAIL,
  type NotificationSettings,
} from "@/lib/admin-api";

export const Route = createFileRoute("/admin/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  const [settings, setSettings] = useState<NotificationSettings>({
    recipientEmails: [""],
    notifyOnApplication: true,
    notifyOnSelected: true,
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    getNotificationSettings()
      .then(setSettings)
      .catch((e) => setError(e instanceof Error ? e.message : "Konnte Einstellungen nicht laden."))
      .finally(() => setLoading(false));
  }, []);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    const emails = settings.recipientEmails.map((x) => x.trim()).filter((x) => x.length > 0);
    if (emails.length === 0) {
      setError("Bitte mindestens eine E-Mail-Adresse angeben.");
      return;
    }
    if (emails.some((x) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(x))) {
      setError("Bitte nur gültige E-Mail-Adressen angeben.");
      return;
    }
    setBusy(true);
    try {
      await saveNotificationSettings({ ...settings, recipientEmails: emails });
      setSettings((s) => ({ ...s, recipientEmails: emails }));
      setNotice("Einstellungen gespeichert.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <div className="text-sm text-muted-foreground">Lädt …</div>;
  }

  return (
    <div className="max-w-xl">
      <div className="eyebrow text-muted-foreground">Backoffice</div>
      <h1 className="mt-2 text-2xl font-medium">Benachrichtigungen</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Lege fest, an welche E-Mail-Adresse Hinweise gesendet werden und für welche
        Ereignisse du benachrichtigt werden möchtest.
      </p>

      <form onSubmit={save} className="mt-8 flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">Empfänger-E-Mails</span>
          <p className="text-xs text-muted-foreground">
            An diese Adressen werden Benachrichtigungen gesendet.
          </p>
          <div className="flex flex-col gap-2">
            {settings.recipientEmails.map((email, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) =>
                    setSettings((s) => ({
                      ...s,
                      recipientEmails: s.recipientEmails.map((x, idx) => (idx === i ? e.target.value : x)),
                    }))
                  }
                  placeholder={NOTIFY_DEFAULT_EMAIL}
                  className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={() =>
                    setSettings((s) => ({
                      ...s,
                      recipientEmails:
                        s.recipientEmails.length > 1
                          ? s.recipientEmails.filter((_, idx) => idx !== i)
                          : [""],
                    }))
                  }
                  className="rounded-md border border-border px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
                  aria-label="Entfernen"
                >
                  Entfernen
                </button>
              </div>
            ))}
          </div>
          <div>
            <button
              type="button"
              onClick={() => setSettings((s) => ({ ...s, recipientEmails: [...s.recipientEmails, ""] }))}
              className="rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-muted"
            >
              + Weitere E-Mail hinzufügen
            </button>
          </div>
        </div>

        <fieldset className="flex flex-col gap-3">
          <legend className="text-sm font-medium mb-1">Benachrichtigen bei</legend>
          <label className="flex items-center gap-3 text-sm">
            <input
              type="checkbox"
              checked={settings.notifyOnApplication}
              onChange={(e) => setSettings((s) => ({ ...s, notifyOnApplication: e.target.checked }))}
              className="h-4 w-4"
            />
            <span>Neue Bewerbungen (Fotograf:innen & Visagist:innen)</span>
          </label>
          <label className="flex items-center gap-3 text-sm">
            <input
              type="checkbox"
              checked={settings.notifyOnSelected}
              onChange={(e) => setSettings((s) => ({ ...s, notifyOnSelected: e.target.checked }))}
              className="h-4 w-4"
            />
            <span>Neue Selected-Einreichungen</span>
          </label>
        </fieldset>

        {error && <div className="text-sm text-destructive">{error}</div>}
        {notice && <div className="text-sm text-green-600">{notice}</div>}

        <div>
          <button
            type="submit"
            disabled={busy}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {busy ? "Speichert …" : "Speichern"}
          </button>
        </div>
      </form>
    </div>
  );
}